import { useEffect, useMemo, useState } from "react";
import runtimeManifest from "../../src-tauri/runtime.lock.json";

export const CHROMIUM_VERSION = runtimeManifest.chromium_version;
const CHROMIUM_MAJOR = CHROMIUM_VERSION.split(".")[0];

export type JsonObject = Record<string, any>;

type OsChoice = "Windows" | "macOS" | "Linux";
type NoiseMode = "real" | "auto";

export type DevicePreset = {
  id: string;
  label: string;
  platform: OsChoice;
  gpu: string;
  payload: JsonObject;
};

const CPU_OPTIONS = [2, 4, 6, 8, 10, 12, 14, 16, 20, 24];
const MEMORY_OPTIONS = [4, 8, 16, 32];
const MEDIA_OPTIONS = [0, 1, 2, 3];
const DEFAULT_PORTS = [
  3389, 5900, 5901, 5800, 7070, 6568, 5938, 1080, 8080, 3128, 3030,
];
const TIMEZONES = [
  "auto",
  "America/Chicago",
  "America/Denver",
  "America/Los_Angeles",
  "America/New_York",
  "America/Sao_Paulo",
  "America/Toronto",
  "Asia/Bangkok",
  "Asia/Dubai",
  "Asia/Hong_Kong",
  "Asia/Jakarta",
  "Asia/Kolkata",
  "Asia/Seoul",
  "Asia/Shanghai",
  "Asia/Singapore",
  "Asia/Tokyo",
  "Australia/Sydney",
  "Europe/Amsterdam",
  "Europe/Athens",
  "Europe/Berlin",
  "Europe/Bucharest",
  "Europe/Helsinki",
  "Europe/Istanbul",
  "Europe/Kyiv",
  "Europe/Lisbon",
  "Europe/London",
  "Europe/Madrid",
  "Europe/Moscow",
  "Europe/Paris",
  "Europe/Prague",
  "Europe/Rome",
  "Europe/Stockholm",
  "Europe/Warsaw",
  "Europe/Vienna",
  "Europe/Zurich",
  "Pacific/Auckland",
  "UTC",
];
const LOCALES = [
  ["auto", "Автоматически по геолокации прокси"],
  ["en-US", "English (US)"],
  ["en-GB", "English (UK)"],
  ["en-CA", "English (Canada)"],
  ["en-AU", "English (Australia)"],
  ["de-DE", "Deutsch (Deutschland)"],
  ["es-ES", "Español (España)"],
  ["es-MX", "Español (México)"],
  ["fr-FR", "Français (France)"],
  ["it-IT", "Italiano"],
  ["nl-NL", "Nederlands"],
  ["pl-PL", "Polski"],
  ["pt-BR", "Português (Brasil)"],
  ["pt-PT", "Português (Portugal)"],
  ["ro-RO", "Română"],
  ["ru-RU", "Русский"],
  ["uk-UA", "Українська"],
  ["tr-TR", "Türkçe"],
  ["el-GR", "Ελληνικά"],
  ["cs-CZ", "Čeština"],
  ["sv-SE", "Svenska"],
  ["fi-FI", "Suomi"],
  ["no-NO", "Norsk"],
  ["da-DK", "Dansk"],
  ["hu-HU", "Magyar"],
  ["zh-CN", "中文 (简体)"],
  ["zh-TW", "中文 (繁體)"],
  ["ja-JP", "日本語"],
  ["ko-KR", "한국어"],
  ["ar-SA", "العربية"],
  ["he-IL", "עברית"],
  ["id-ID", "Bahasa Indonesia"],
  ["vi-VN", "Tiếng Việt"],
  ["th-TH", "ไทย"],
  ["hi-IN", "हिन्दी"],
] as const;

const OS_DEFAULTS: Record<
  OsChoice,
  { platform: string; server: string; userAgent: string }
> = {
  Windows: {
    platform: "Win32",
    server: "WINDOWS",
    userAgent:
      `Mozilla/5.0 (Windows NT 10.0; Win64; x64) AppleWebKit/537.36 (KHTML, like Gecko) Chrome/${CHROMIUM_MAJOR}.0.0.0 Safari/537.36`,
  },
  macOS: {
    platform: "MacIntel",
    server: "MACOS",
    userAgent:
      `Mozilla/5.0 (Macintosh; Intel Mac OS X 10_15_7) AppleWebKit/537.36 (KHTML, like Gecko) Chrome/${CHROMIUM_MAJOR}.0.0.0 Safari/537.36`,
  },
  Linux: {
    platform: "Linux x86_64",
    server: "LINUX",
    userAgent:
      `Mozilla/5.0 (X11; Linux x86_64) AppleWebKit/537.36 (KHTML, like Gecko) Chrome/${CHROMIUM_MAJOR}.0.0.0 Safari/537.36`,
  },
};

function clone(value: JsonObject): JsonObject {
  return JSON.parse(JSON.stringify(value ?? {})) as JsonObject;
}

function inferOs(config: JsonObject): OsChoice {
  const raw = String(
    config.client_hints?.platform ?? config.navigator?.platform ?? "",
  ).toLowerCase();
  if (raw.includes("linux")) return "Linux";
  if (raw.includes("mac")) return "macOS";
  return "Windows";
}

function acceptLanguage(locale: string): string | null {
  if (locale === "auto") return null;
  const base = locale.split("-")[0];
  return locale === "en-US"
    ? "en-US,en;q=0.9"
    : `${locale},${base};q=0.9,en-US;q=0.8,en;q=0.7`;
}

function languages(locale: string): string[] | null {
  if (locale === "auto") return null;
  const base = locale.split("-")[0];
  return locale === "en-US" ? ["en-US", "en"] : [locale, base, "en-US", "en"];
}

export function parsePorts(value: string): number[] {
  return [
    ...new Set(
      value
        .split(/[\s,;]+/)
        .map((part) => Number.parseInt(part, 10))
        .filter((port) => Number.isInteger(port) && port >= 1 && port <= 65535),
    ),
  ].sort((left, right) => left - right);
}

function runtimeClientHints() {
  const parts = CHROMIUM_VERSION.split(".");
  return { brand_version: parts[0], brand_full_version: CHROMIUM_VERSION, chrome_build: Number(parts[2]), chrome_patch: Number(parts[3]), grease_brand: runtimeManifest.grease_brand, grease_version: runtimeManifest.grease_version, grease_full_version: `${runtimeManifest.grease_version}.0.0.0` };
}

export function alignFingerprintRuntime(source: JsonObject): JsonObject {
  const config = clone(source);
  const os = inferOs(config);
  const ua = config.navigator?.user_agent;
  config.navigator = { ...(config.navigator ?? {}), user_agent: typeof ua === "string" && /Chrome\/[\d.]+/.test(ua) ? ua.replace(/Chrome\/[\d.]+/, `Chrome/${CHROMIUM_MAJOR}.0.0.0`) : OS_DEFAULTS[os].userAgent };
  config.client_hints = { ...(config.client_hints ?? {}), ...runtimeClientHints() };
  if (config.tls) config.tls = { ...config.tls, ...runtimeManifest.tls };
  return config;
}

export function createDefaultFingerprintConfig(): JsonObject {
  const defaults = OS_DEFAULTS.Windows;
  return {
    timezone: "auto",
    icu_locale: null,
    webrtc: "auto",
    navigator: {
      platform: defaults.platform,
      language: "auto",
      accept_language: null,
      languages: null,
      user_agent: defaults.userAgent,
      hardware_concurrency: 6,
      device_memory: 16,
      do_not_track: "1",
    },
    client_hints: { platform: "Windows", ...runtimeClientHints() },
    media_devices: {
      audio_input_count: 1,
      audio_output_count: 1,
      video_input_count: 1,
    },
    geolocation: { mode: "auto" },
    noise: {
      canvas: { enabled: false, seed: 0 },
      webgl: { enabled: false, seed: 0, intensity: 0 },
      audio: { enabled: false, seed: 0 },
      client_rects: { enabled: false, seed: 0, max_offset: 0 },
      sensors: { enabled: false, seed: 0 },
      fonts: { enabled: false, seed: 0 },
    },
    blocked_ports: [...DEFAULT_PORTS],
  };
}

export function applyFingerprintBasics(
  source: JsonObject,
  values: {
    cpu: number;
    memory: number;
    timezone: string;
    language: string;
    doNotTrack: boolean;
  },
): JsonObject {
  const next = clone(source);
  next.timezone = values.timezone;
  next.icu_locale = values.language === "auto" ? null : values.language;
  next.navigator = {
    ...(next.navigator ?? {}),
    hardware_concurrency: values.cpu,
    device_memory: values.memory,
    language: values.language,
    accept_language: acceptLanguage(values.language),
    languages: languages(values.language),
    do_not_track: values.doNotTrack ? "1" : null,
  };
  return next;
}

/** Selecting a GPU also applies its screen, OS and WebGL capabilities. Mixing
 * the GPU from one preset with the screen/OS of another would be incoherent. */
export function applyDevicePreset(preset: DevicePreset): JsonObject {
  const config = alignFingerprintRuntime(preset.payload);
  config.notes = "";
  return config;
}

export function deviceOptionLabel(preset: DevicePreset): string {
  if (preset.label !== preset.id) return preset.label;
  const normalized = preset.gpu.replace(/\((?:TM|R)\)/gi, "");
  const model = normalized.match(
    /(?:NVIDIA\s+){0,2}(?:GeForce|Quadro|RTX|GTX|GT)\s+[^,(]+|(?:AMD\s+)?Radeon\s+[^,(]+|Intel\s+(?:Arc|Iris|HD|UHD)\s+[^,(]+|Apple\s+M[1-9]\s*[^,(]*/i,
  )?.[0].trim().replace(/\s+/g, " ");
  return model ? `${model} · ${preset.id}` : preset.label;
}

function NumberSelect({
  value,
  options,
  onChange,
}: {
  value: number;
  options: number[];
  onChange: (value: number) => void;
}) {
  return (
    <select
      value={value}
      onChange={(event) => onChange(Number(event.target.value))}
    >
      {options.map((option) => (
        <option key={option}>{option}</option>
      ))}
    </select>
  );
}

function NoiseSelect({
  value,
  onChange,
}: {
  value: NoiseMode;
  onChange: (value: NoiseMode) => void;
}) {
  return (
    <select
      value={value}
      onChange={(event) => onChange(event.target.value as NoiseMode)}
    >
      <option value="real">Без изменений</option>
      <option value="auto">Автошум</option>
    </select>
  );
}

export function ProfileConfigurationEditor({
  platformConstraints,
  fingerprintConfig,
  devicePresets,
  onPlatformChange,
  onFingerprintChange,
}: {
  platformConstraints: JsonObject;
  fingerprintConfig: JsonObject;
  devicePresets: DevicePreset[];
  onPlatformChange: (value: JsonObject) => void;
  onFingerprintChange: (value: JsonObject) => void;
}) {
  const fingerprint = fingerprintConfig;
  const navigatorConfig = fingerprint.navigator ?? {};
  const noise = fingerprint.noise ?? {};
  const media = fingerprint.media_devices ?? {};
  const geo = fingerprint.geolocation ?? {};
  const os = inferOs(fingerprint);
  const uaMajor = /Chrome\/(\d+)/.exec(navigatorConfig.user_agent ?? "")?.[1];
  const versionMismatch = (uaMajor && uaMajor !== CHROMIUM_MAJOR) || (fingerprint.client_hints?.brand_full_version && fingerprint.client_hints.brand_full_version !== CHROMIUM_VERSION);
  const visibleDevices = devicePresets.filter((preset) => preset.platform === os);
  const currentDevice = visibleDevices.find(
    (preset) =>
      preset.payload.webgl?.renderer === fingerprint.webgl?.renderer &&
      preset.payload.webgl?.vendor === fingerprint.webgl?.vendor &&
      preset.payload.navigator?.platform === fingerprint.navigator?.platform &&
      JSON.stringify(preset.payload.screen ?? null) ===
        JSON.stringify(fingerprint.screen ?? null),
  );
  const [ports, setPorts] = useState(() =>
    (fingerprint.blocked_ports ?? DEFAULT_PORTS).join(", "),
  );
  const [platformJson, setPlatformJson] = useState(() =>
    JSON.stringify(platformConstraints ?? {}, null, 2),
  );
  const [fingerprintJson, setFingerprintJson] = useState(() =>
    JSON.stringify(fingerprint, null, 2),
  );
  const [jsonError, setJsonError] = useState("");
  const platformSignature = useMemo(
    () => JSON.stringify(platformConstraints),
    [platformConstraints],
  );
  const fingerprintSignature = useMemo(
    () => JSON.stringify(fingerprint),
    [fingerprint],
  );
  const fingerprintPortsText = (
    Array.isArray(fingerprint.blocked_ports)
      ? fingerprint.blocked_ports.filter(
          (port: unknown) => typeof port === "number",
        )
      : DEFAULT_PORTS
  ).join(", ");
  const portsSignature = JSON.stringify(parsePorts(fingerprintPortsText));

  useEffect(
    () => setPlatformJson(JSON.stringify(platformConstraints, null, 2)),
    [platformSignature, platformConstraints],
  );
  useEffect(
    () => setFingerprintJson(JSON.stringify(fingerprint, null, 2)),
    [fingerprintSignature, fingerprint],
  );
  useEffect(() => {
    if (JSON.stringify(parsePorts(ports)) !== portsSignature) {
      setPorts(fingerprintPortsText);
    }
  }, [fingerprintPortsText, ports, portsSignature]);

  const updateFingerprint = (recipe: (next: JsonObject) => void) => {
    const next = clone(fingerprint);
    recipe(next);
    onFingerprintChange(next);
  };
  const updateNavigator = (key: string, value: unknown) =>
    updateFingerprint((next) => {
      next.navigator = { ...(next.navigator ?? {}), [key]: value };
    });
  const chooseOs = (choice: OsChoice) => {
    const first = devicePresets.find((preset) => preset.platform === choice);
    if (first) {
      onFingerprintChange(applyDevicePreset(first));
      return;
    }
    const defaults = createDefaultFingerprintConfig();
    defaults.navigator.platform = OS_DEFAULTS[choice].platform;
    defaults.navigator.user_agent = OS_DEFAULTS[choice].userAgent;
    defaults.client_hints.platform = choice;
    onFingerprintChange(defaults);
  };
  const chooseDevice = (id: string) => {
    const device = visibleDevices.find((preset) => preset.id === id);
    if (device) onFingerprintChange(applyDevicePreset(device));
  };
  const choosePlatform = (server: string) => {
    const next = clone(platformConstraints ?? {});
    if (!server) delete next.platforms;
    else next.platforms = [server];
    onPlatformChange(next);
  };
  const chooseArchitecture = (architecture: string) => {
    const next = clone(platformConstraints ?? {});
    if (!architecture) delete next.architectures;
    else next.architectures = [architecture];
    onPlatformChange(next);
  };
  const setMinimumVersion = (version: string) => {
    const next = clone(platformConstraints ?? {});
    if (version.trim()) next.minimum_client_version = version.trim();
    else delete next.minimum_client_version;
    onPlatformChange(next);
  };
  const setLanguage = (language: string) =>
    updateFingerprint((next) => {
      next.icu_locale = language === "auto" ? null : language;
      next.navigator = {
        ...(next.navigator ?? {}),
        language,
        accept_language: acceptLanguage(language),
        languages: languages(language),
      };
    });
  const setNoise = (key: string, mode: NoiseMode) =>
    updateFingerprint((next) => {
      const enabled = mode === "auto";
      next.noise = {
        ...(next.noise ?? {}),
        [key]: {
          ...(next.noise?.[key] ?? {}),
          enabled,
          seed: 0,
          ...(key === "webgl" ? { intensity: enabled ? 0.0005 : 0 } : {}),
          ...(key === "client_rects" ? { max_offset: enabled ? 1 : 0 } : {}),
        },
      };
    });
  const applyJson = () => {
    try {
      const nextPlatform = JSON.parse(platformJson);
      const nextFingerprint = JSON.parse(fingerprintJson);
      if (
        !nextPlatform ||
        Array.isArray(nextPlatform) ||
        typeof nextPlatform !== "object"
      )
        throw new Error("Ограничения платформы должны быть JSON-объектом.");
      if (
        !nextFingerprint ||
        Array.isArray(nextFingerprint) ||
        typeof nextFingerprint !== "object"
      )
        throw new Error("Конфигурация отпечатка должна быть JSON-объектом.");
      onPlatformChange(nextPlatform);
      onFingerprintChange(nextFingerprint);
      setJsonError("");
    } catch (reason) {
      setJsonError(
        reason instanceof Error ? reason.message : "Проверьте JSON.",
      );
    }
  };

  const platform = platformConstraints.platforms?.[0] ?? "";
  const architecture = platformConstraints.architectures?.[0] ?? "";
  const renderer = fingerprint.webgl?.renderer;
  const noiseMode = (key: string): NoiseMode =>
    noise[key]?.enabled ? "auto" : "real";

  return (
    <div className="configuration-editor">
      <section className="configuration-platform">
        <header>
          <strong>Совместимость запуска</strong>
          <small>
            Оставьте «Любая», если профиль должен запускаться на всех клиентах.
          </small>
        </header>
        <div className="configuration-fields three">
          <label className="field">
            <span>Платформа клиента</span>
            <select
              value={platform}
              onChange={(event) => choosePlatform(event.target.value)}
            >
              <option value="">Любая</option>
              <option value="WINDOWS">Windows</option>
              <option value="MACOS">macOS</option>
              <option value="LINUX">Linux</option>
            </select>
          </label>
          <label className="field">
            <span>Архитектура</span>
            <select
              value={architecture}
              onChange={(event) => chooseArchitecture(event.target.value)}
            >
              <option value="">Любая</option>
              <option value="X86_64">x86-64</option>
              <option value="AARCH64">ARM64</option>
              <option value="I686">x86</option>
              <option value="ARMV7">ARMv7</option>
            </select>
          </label>
          <label className="field">
            <span>Минимальная версия</span>
            <input
              value={platformConstraints.minimum_client_version ?? ""}
              onChange={(event) => setMinimumVersion(event.target.value)}
              placeholder="например, 0.1.10"
            />
          </label>
        </div>
      </section>
      <div className="configuration-grid">
        <section>
          <h3>Устройство</h3>
          <label className="field">
            <span>Операционная система отпечатка</span>
            <div className="configuration-segments">
              {(["macOS", "Windows", "Linux"] as OsChoice[]).map((choice) => (
                <button
                  type="button"
                  key={choice}
                  className={os === choice ? "active" : ""}
                  onClick={() => chooseOs(choice)}
                >
                  {choice}
                </button>
              ))}
            </div>
          </label>
          <label className="field">
            <span>GPU / устройство</span>
            <select value={currentDevice?.id ?? ""} onChange={(event) => chooseDevice(event.target.value)}>
              <option value="" disabled={Boolean(currentDevice)}>
                {renderer && !currentDevice ? "GPU текущего шаблона / профиля" : "Автоматически / из выбранного шаблона"}
              </option>
              {visibleDevices.map((preset) => (
                <option key={preset.id} value={preset.id}>{deviceOptionLabel(preset)}</option>
              ))}
            </select>
            <small>
              {visibleDevices.length === 0
                ? `Для ${os} устройства ещё не импортированы на сервер.`
                : renderer && !currentDevice
                  ? `Текущий GPU из шаблона или готового профиля: ${renderer}`
                  : "Выбор применяет вместе GPU, экран и возможности выбранного устройства."}
            </small>
          </label>
          <label className="field">
            <span>User-Agent · Chromium {CHROMIUM_VERSION}</span>
            <input
              className="mono-input"
              value={navigatorConfig.user_agent ?? ""}
              placeholder="Определяется движком, если не задан"
              onChange={(event) =>
                updateNavigator("user_agent", event.target.value)
              }
            />
          </label>
          {versionMismatch && <div className="alert warning"><span>Версия отпечатка отличается от Chromium {CHROMIUM_VERSION}. Согласуйте её перед следующим запуском профиля.</span><button type="button" onClick={() => onFingerprintChange(alignFingerprintRuntime(fingerprint))}>Согласовать версии</button></div>}
          <div className="configuration-fields two">
            <label className="field">
              <span>Ядра CPU</span>
              <NumberSelect
                value={navigatorConfig.hardware_concurrency ?? 6}
                options={CPU_OPTIONS}
                onChange={(value) =>
                  updateNavigator("hardware_concurrency", value)
                }
              />
            </label>
            <label className="field">
              <span>Память, ГБ</span>
              <NumberSelect
                value={navigatorConfig.device_memory ?? 16}
                options={MEMORY_OPTIONS}
                onChange={(value) => updateNavigator("device_memory", value)}
              />
            </label>
          </div>
        </section>
        <section>
          <h3>Локализация и отпечаток</h3>
          <div className="configuration-fields two">
            <label className="field">
              <span>Часовой пояс</span>
              <select
                value={fingerprint.timezone ?? "auto"}
                onChange={(event) =>
                  updateFingerprint((next) => {
                    next.timezone = event.target.value;
                  })
                }
              >
                {[...new Set([typeof fingerprint.timezone === "string" ? fingerprint.timezone : "auto", ...TIMEZONES])].map((timezone) => (
                  <option key={timezone} value={timezone}>
                    {timezone === "auto" ? "Автоматически по прокси" : timezone}
                  </option>
                ))}
              </select>
            </label>
            <label className="field">
              <span>Язык</span>
              <select
                value={navigatorConfig.language ?? "auto"}
                onChange={(event) => setLanguage(event.target.value)}
              >
                {LOCALES.map(([value, label]) => (
                  <option key={value} value={value}>
                    {label}
                  </option>
                ))}
              </select>
            </label>
          </div>
          <div className="configuration-noise">
            {[
              ["canvas", "Canvas"],
              ["webgl", "WebGL"],
              ["audio", "Audio"],
              ["client_rects", "Геометрия"],
              ["sensors", "Датчики"],
              ["fonts", "Шрифты"],
            ].map(([key, label]) => (
              <label className="field" key={key}>
                <span>{label}</span>
                <NoiseSelect
                  value={noiseMode(key)}
                  onChange={(value) => setNoise(key, value)}
                />
              </label>
            ))}
          </div>
          <label className="field">
            <span>Блокируемые порты</span>
            <input
              value={ports}
              onChange={(event) => {
                const value = event.target.value;
                setPorts(value);
                updateFingerprint((next) => {
                  next.blocked_ports = parsePorts(value);
                });
              }}
            />
          </label>
        </section>
        <section>
          <h3>Приватность</h3>
          <div className="configuration-fields two">
            <label className="field">
              <span>WebRTC</span>
              <select
                value={fingerprint.webrtc ?? "auto"}
                onChange={(event) =>
                  updateFingerprint((next) => {
                    next.webrtc = event.target.value;
                  })
                }
              >
                <option value="auto">Автоматически</option>
                <option value="tcp_only">Только TCP</option>
                <option value="block">Блокировать</option>
              </select>
            </label>
            <label className="field">
              <span>Do Not Track</span>
              <select
                value={navigatorConfig.do_not_track ? "1" : "0"}
                onChange={(event) =>
                  updateNavigator(
                    "do_not_track",
                    event.target.value === "1" ? "1" : null,
                  )
                }
              >
                <option value="1">Включено</option>
                <option value="0">Выключено</option>
              </select>
            </label>
          </div>
          <label className="field">
            <span>Геолокация</span>
            <div className="configuration-segments">
              <button
                type="button"
                className={geo.mode !== "manual" ? "active" : ""}
                onClick={() =>
                  updateFingerprint((next) => {
                    next.geolocation = { mode: "auto" };
                  })
                }
              >
                По прокси
              </button>
              <button
                type="button"
                className={geo.mode === "manual" ? "active" : ""}
                onClick={() =>
                  updateFingerprint((next) => {
                    next.geolocation = {
                      mode: "manual",
                      latitude: 52.2297,
                      longitude: 21.0122,
                      accuracy: 50,
                    };
                  })
                }
              >
                Вручную
              </button>
            </div>
          </label>
          {geo.mode === "manual" && (
            <div className="configuration-fields three">
              <label className="field">
                <span>Широта</span>
                <input
                  type="number"
                  step="0.0001"
                  value={geo.latitude ?? 52.2297}
                  onChange={(event) =>
                    updateFingerprint((next) => {
                      next.geolocation = {
                        ...next.geolocation,
                        latitude: Number(event.target.value),
                      };
                    })
                  }
                />
              </label>
              <label className="field">
                <span>Долгота</span>
                <input
                  type="number"
                  step="0.0001"
                  value={geo.longitude ?? 21.0122}
                  onChange={(event) =>
                    updateFingerprint((next) => {
                      next.geolocation = {
                        ...next.geolocation,
                        longitude: Number(event.target.value),
                      };
                    })
                  }
                />
              </label>
              <label className="field">
                <span>Точность, м</span>
                <input
                  type="number"
                  value={geo.accuracy ?? 50}
                  onChange={(event) =>
                    updateFingerprint((next) => {
                      next.geolocation = {
                        ...next.geolocation,
                        accuracy: Number(event.target.value),
                      };
                    })
                  }
                />
              </label>
            </div>
          )}
          <h3 className="configuration-subtitle">Медиаустройства</h3>
          <div className="configuration-fields three">
            <label className="field">
              <span>Микрофоны</span>
              <NumberSelect
                value={media.audio_input_count ?? 1}
                options={MEDIA_OPTIONS}
                onChange={(value) =>
                  updateFingerprint((next) => {
                    next.media_devices = {
                      ...(next.media_devices ?? {}),
                      audio_input_count: value,
                    };
                  })
                }
              />
            </label>
            <label className="field">
              <span>Динамики</span>
              <NumberSelect
                value={media.audio_output_count ?? 1}
                options={MEDIA_OPTIONS}
                onChange={(value) =>
                  updateFingerprint((next) => {
                    next.media_devices = {
                      ...(next.media_devices ?? {}),
                      audio_output_count: value,
                    };
                  })
                }
              />
            </label>
            <label className="field">
              <span>Камеры</span>
              <NumberSelect
                value={media.video_input_count ?? 1}
                options={MEDIA_OPTIONS}
                onChange={(value) =>
                  updateFingerprint((next) => {
                    next.media_devices = {
                      ...(next.media_devices ?? {}),
                      video_input_count: value,
                    };
                  })
                }
              />
            </label>
          </div>
        </section>
      </div>
      <details className="configuration-advanced">
        <summary>Расширенный режим JSON</summary>
        <p>
          Нужен только для редких параметров, которых нет в форме. После ручной
          правки нажмите «Применить JSON».
        </p>
        <div className="configuration-json">
          <label className="field">
            <span>Ограничения платформы</span>
            <textarea
              value={platformJson}
              onChange={(event) => setPlatformJson(event.target.value)}
              rows={10}
              spellCheck={false}
            />
          </label>
          <label className="field">
            <span>Конфигурация отпечатка</span>
            <textarea
              value={fingerprintJson}
              onChange={(event) => setFingerprintJson(event.target.value)}
              rows={10}
              spellCheck={false}
            />
          </label>
        </div>
        {jsonError && <div className="inline-error">{jsonError}</div>}
        <button type="button" className="secondary compact" onClick={applyJson}>
          Применить JSON
        </button>
      </details>
    </div>
  );
}
